import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, CardContent, CardHeader, Stack, Tab } from '@mui/material';
import { MprcBarTabs, MprcText } from 'components';
import { MentionBlot } from "components/Quill/Mention/mentionBlot";
import { CalloutDto, DiscussionListFilter, LmsCalloutType, TrainingType } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { MentionData } from "views/knowledge/publication/creation/components/PublicationEditor";
import { ClassroomDiscussionBox } from 'views/lms/components/discussion/ClassroomDiscussionBox';
import { DiscussionMentionEvent, subscribeDiscussionMentionEvent, unsubscribeDiscussionMentionEvent } from "views/lms/components/discussion/LmsDiscussionEvent";
import { useEnrollmentViewContext } from "..";
import { CourseEnrollmentViewSearch } from "../Course/CourseEnrollmentViewSearch";
import { EnrollmentNotices } from "./EnrollmentNotices";
import { EnrollmentViewOverview } from './EnrollmentViewOverview';
import { MATERIAL_LIST_CONTAINER_ID, MaterialListRibbons } from "./MaterialListRibbons";

/////////////////////////////////////////////////////////////////////////////////////

const aboutTabs = ["overview", "discussion", "notices", "materialList", "search"] as const;

export type EnrollmentViewAboutTabs = (typeof aboutTabs)[number];

export function isOfTypeEnrollmentViewAboutTabs(value: string): value is EnrollmentViewAboutTabs {
    return (aboutTabs as readonly string[]).includes(value);
}

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    openOnTab?: EnrollmentViewAboutTabs;
    discussionFilter?: DiscussionListFilter;
}

/////////////////////////////////////////////////////////////////////////////////////

export const EnrollmentViewAbout: React.FC<IProps> = ({ openOnTab, discussionFilter }) => {

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();

    const { enrollment, trainingType } = useEnrollmentViewContext();
    const { t } = useTranslation("lms");

    const [currentTab, setCurrentTab] = useState<EnrollmentViewAboutTabs>(openOnTab ?? (location.state?.saveState?.source == "materialList" ? "materialList" : "overview"));
    const [callouts, setCallouts] = useState<CalloutDto[]>([]);

    const discussionInitialValueRef = useRef<string>("");

    /////////////////////////////////////////////////////////////////////////////////////

    // Limpar navigation state
    if (location.state?.saveState?.source == "materialList") navigate(location.pathname, { replace: true });

    ///
    const setDiscussionInitialValue = (e: DiscussionMentionEvent) => {
        if (e.detail) {
            var node = MentionBlot.create({ id: e.detail.userId, denotationChar: "@", ot: "user", value: e.detail.name } as MentionData);
            discussionInitialValueRef.current = (node as HTMLElement).outerHTML;
        }
    };

    const handleClickTab = ((e: React.SyntheticEvent, value: EnrollmentViewAboutTabs) => {
        setCurrentTab(value);


        if (!enrollment.isPreview && (value == "discussion" || value == "notices" || value == "materialList")) {

            var calloutType = value == "discussion"
                ? LmsCalloutType.EnrollmentDiscussion
                : value == "materialList"
                    ? LmsCalloutType.EnrollmentMaterialList
                    : LmsCalloutType.EnrollmentNotices;

            setCallouts([...callouts.filter(p => LmsCalloutType[p.id as keyof typeof LmsCalloutType] != calloutType)]);

            api.v1.lmsStudent.saveCallout({
                calloutId: enrollment.enrollmentId,
                calloutType
            });
        }
    });


    const buildTabName = (label: string, calloutType: LmsCalloutType) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <span>{label}</span>
                {callouts.find(p => LmsCalloutType[p.id as keyof typeof LmsCalloutType] == calloutType && p.active == true) != null
                    && <MprcText color="warning">
                        <FontAwesomeIcon icon={faCircle} size="xs" color="warning" />
                    </MprcText>
                }
            </Stack>
        );
    };

    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        subscribeDiscussionMentionEvent(setDiscussionInitialValue);
        return () => {
            unsubscribeDiscussionMentionEvent(setDiscussionInitialValue);
        };
    }, []);

    useEffect(() => {
        discussionInitialValueRef.current = "";
    }, [currentTab]);

    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        let poolingCallout: ReturnType<typeof setTimeout> | null = null;

        function getCallouts() {
            api.v1.enrollment.getEnrollmentCallouts({
                enrollmentId: enrollment!.enrollmentId,
            }).then(response => setCallouts(response.data));
        }

        if (enrollment != null && !enrollment.isPreview) {
            poolingCallout = setInterval(getCallouts, 30000);
            getCallouts();
        }

        return () => {
            if (poolingCallout) clearTimeout(poolingCallout);
        };
    }, [enrollment]);

    return (
        <Card elevation={trainingType == TrainingType.LearningPath ? 0 : 4}>
            <CardHeader
                title={
                    <MprcBarTabs
                        value={currentTab}
                        onChange={handleClickTab}
                        variant={trainingType == TrainingType.LearningPath ? "fullWidth" : "standard"}
                    >
                        <Tab
                            value={"overview"}
                            label={t("Student.EnrollmentView.OverviewTitle")}
                        />
                        {
                            enrollment.discussionEnable &&
                            <Tab
                                value={"discussion"}
                                label={buildTabName(t("Student.EnrollmentView.DiscussionTitle"), LmsCalloutType.EnrollmentDiscussion)}
                            />
                        }
                        {enrollment.hasNotices &&
                            <Tab
                                value={"notices"}
                                label={buildTabName(t("Student.EnrollmentView.NoticesTitle"), LmsCalloutType.EnrollmentNotices)}
                            />
                        }
                        {trainingType != TrainingType.LearningPath && enrollment.hasMaterialList &&
                            <Tab
                                value={"materialList"}
                                label={buildTabName(t("Student.EnrollmentView.SupportMaterialTitle"), LmsCalloutType.EnrollmentMaterialList)}
                            />
                        }
                        {trainingType == TrainingType.Course && enrollment.course?.hasPublicationContent === true &&
                            <Tab value={"search"} icon={<FontAwesomeIcon icon={faMagnifyingGlass} />} />
                        }
                    </MprcBarTabs>
                }
            />
            <CardContent id={MATERIAL_LIST_CONTAINER_ID} sx={{ paddingTop: 0 }}>
                {currentTab === "overview" &&
                    <EnrollmentViewOverview onChangeTab={(tab) => setCurrentTab(tab)} />
                }
                {currentTab === "discussion" &&
                    <ClassroomDiscussionBox
                        classroomId={enrollment.classroomId}
                        enrollmentId={enrollment.isPreview ? null : enrollment.enrollmentId}
                        enableInstructorPrivacyOption={enrollment.trainingType == TrainingType.Course}
                        disableEditPrivacy={enrollment.trainingType == TrainingType.Course ? enrollment.discussionPrivate : false}
                        initialValue={discussionInitialValueRef.current}
                        initialListFilter={discussionFilter}
                        discussionMode="student"
                    />
                }
                {currentTab === "notices" &&
                    <EnrollmentNotices />
                }
                {currentTab === "materialList" &&
                    <MaterialListRibbons classroomId={enrollment.classroomId} />
                }
                {currentTab === "search" &&
                    <CourseEnrollmentViewSearch />
                }
            </CardContent>
        </Card>
    );
};
